import { WorkRuleSetRule } from "@rhumbix/rmbx_work_rules"
import { AllExceptions, AnyException, Thunk, TkSummary } from "../common/types"
import { _get } from "../api/util"

interface iToggleException {
    type: "TOGGLE_EXCEPTION"
    whichException: keyof AllExceptions
}

interface iSetExceptionValue {
    type: "SET_EXCEPTION_VALUE"
    exception: AnyException
}

interface iSetTkSummary {
    type: "SET_TK_SUMMARY"
    data: any
}

interface iMergeTkSummary {
    type: "MERGE_TK_SUMMARY"
    data: TkSummary
    employeeIds: number[]
}

interface iSetWorkRuleOverride {
    type: "SET_WORK_RULE_OVERRIDE"
    data: WorkRuleSetRule[]
}

interface iClearWorkRuleOverride {
    type: "CLEAR_WORK_RULE_OVERRIDE"
}

interface iFilterExceptions {
    type: "TOGGLE_FILTER_EXCEPTIONS"
}

export const setExceptionValue = (exception: AnyException): iSetExceptionValue => {
    return {
        type: "SET_EXCEPTION_VALUE",
        exception,
    }
}

export const toggleException = (whichException: keyof AllExceptions): iToggleException => {
    return {
        type: "TOGGLE_EXCEPTION",
        whichException,
    }
}

export const toggleFilterExceptions = (): iFilterExceptions => {
    return {
        type: "TOGGLE_FILTER_EXCEPTIONS",
    }
}

export const setTkSummary = (tkSummary: TkSummary): iSetTkSummary => {
    return {
        type: "SET_TK_SUMMARY",
        data: tkSummary,
    }
}

/**
 * Merge a per-employee timecard summary into the existing store summary. The listed
 * `employeeIds` are cleared before overlaying `tkSummary`, so an employee who no longer has
 * any rows (the endpoint omits employees with no data) doesn't retain stale rows.
 *
 * @param tkSummary The freshly fetched summary, keyed by employee ID.
 * @param employeeIds The employees that were requested (the authoritative set to refresh).
 */
export const mergeTkSummary = (tkSummary: TkSummary, employeeIds: number[]): iMergeTkSummary => {
    return {
        type: "MERGE_TK_SUMMARY",
        data: tkSummary,
        employeeIds,
    }
}

/**
 * Fetch the timecard summary and apply it to the store.
 *
 * When `employeeIds` is non-empty AND a summary is already loaded, only those employees are
 * requested and the response is merged into the existing summary. This keeps the (expensive)
 * endpoint from scanning every coworker after a localized edit while preserving the rest of
 * the view's data. Otherwise the full summary is fetched and replaces the store value — this
 * is the correct behavior for the initial load and whenever there is no baseline to merge into.
 *
 * @param requestParams The tk_summary query params (date range, group_by, etc.), without `employee_id`.
 * @param employeeIds The employees to refresh, or null/empty to fetch the full summary.
 */
export const refreshTkSummary = (requestParams: Record<string, unknown>, employeeIds: number[] | null = null): Thunk<Promise<void>> => (dispatch, getState) => {
    const hasBaseline = !!getState().exceptions.tkSummary
    const useSubset = !!employeeIds && employeeIds.length > 0 && hasBaseline
    const params = useSubset ? { ...requestParams, employee_id: employeeIds } : requestParams
    return _get("/api/v4/employees/tk_summary/", params)
        .then((response: TkSummary) => {
            if (useSubset) {
                dispatch(mergeTkSummary(response, employeeIds as number[]))
            } else {
                dispatch(setTkSummary(response))
            }
        })
        .catch((error: unknown) => {
            console.error("Error refreshing TK summary:", error)
        })
}

export const setWorkRuleOverride = (workRuleValues: WorkRuleSetRule[]): iSetWorkRuleOverride => {
    return {
        type: "SET_WORK_RULE_OVERRIDE",
        data: workRuleValues,
    }
}

export const clearWorkRuleOverride = (): iClearWorkRuleOverride => {
    return {
        type: "CLEAR_WORK_RULE_OVERRIDE",
    }
}

export type ExceptionAction = iToggleException | iSetExceptionValue | iFilterExceptions | iSetTkSummary | iMergeTkSummary | iSetWorkRuleOverride | iClearWorkRuleOverride
